import { isSessionBlocked } from '@/core';
import { ApiError, clearSession, getSession, onSessionChange, type SessionState } from '@/core';
import { ensureCsrfToken, loadCurrentUser } from '@/api/auth';
import { ref, type Ref } from 'vue';

// Inicialização preguiçosa: o guard (`ensureSession`) roda ANTES de qualquer componente montar e
// pode já ter chamado setSession — criar o ref no topo do módulo congelaria o estado inicial e a
// view nunca veria o usuário. O ref é criado na primeira chamada, lendo o estado corrente.
let sessionRef: Ref<SessionState> | undefined;
let subscribed = false;

export function useSessionStore(): { session: Ref<SessionState>; signOut: () => void } {
  if (sessionRef === undefined) {
    sessionRef = ref<SessionState>(getSession());
  }
  const session = sessionRef;
  if (!subscribed) {
    subscribed = true;
    onSessionChange((value) => { session.value = value; });
  }
  return { session, signOut: clearSession };
}

export type SessionGuardResult =
  | { readonly status: 'authenticated' }
  | { readonly status: 'unauthenticated' }
  /** Falha transiente COM sessão em memória: as views já têm dados, a navegação segue. */
  | { readonly status: 'unavailable'; readonly error: ApiError }
  /** Falha transiente SEM nada em memória (reload frio): não há shell a mostrar, só a tela de indisponibilidade. */
  | { readonly status: 'unreachable'; readonly error: ApiError };

/**
 * Falha transiente (rede, timeout, CORS, 5xx) não é sessão inválida: o BFF pode estar fora do ar
 * por um instante. Deslogar nesse caso expulsaria quem estava logado.
 */
function isTransient(error: unknown): error is ApiError {
  if (!(error instanceof ApiError)) return false;
  return error.kind !== 'http' || error.status >= 500;
}

/**
 * Guard de rota. Num reload o estado em memória é zerado, mas o cookie de sessão do BFF continua
 * válido — daí a revalidação por `/auth/me`. O token CSRF é rehidratado ANTES de liberar a
 * navegação (`GET /auth/csrf`): sem ele a sessão parece boa e toda mutação toma 403. A busca do
 * CSRF vem primeiro porque o refresh silencioso do httpClient também depende do header.
 *
 * Quatro desfechos, nunca um booleano solto: sessão válida, sessão inválida (401/contrato quebrado
 * → limpa o estado e manda ao login) e indisponibilidade transiente (rede/5xx → preserva a
 * sessão e devolve o erro para a apresentação tratar com estado de erro e retry, em `unavailable`
 * quando havia sessão em memória ou em `unreachable` no reload frio).
 */
export async function ensureSession(): Promise<SessionGuardResult> {
  if (isSessionBlocked()) return { status: 'unauthenticated' };
  const current = getSession();
  const hasIdentity = current.accessToken !== null && current.user !== null;
  if (hasIdentity && current.csrf !== null) return { status: 'authenticated' };
  try {
    await ensureCsrfToken();
    if (!hasIdentity) await loadCurrentUser();
    if (isSessionBlocked()) return { status: 'unauthenticated' };
    return { status: 'authenticated' };
  } catch (error) {
    // Com identidade em memória o usuário segue navegando (blip do BFF); sem identidade não há
    // nada para mostrar — o guard leva à tela de indisponibilidade, nunca ao login nem ao shell vazio.
    if (isTransient(error)) return hasIdentity ? { status: 'unavailable', error } : { status: 'unreachable', error };
    clearSession();
    return { status: 'unauthenticated' };
  }
}
